<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDetectKeyboardLayout } from 'dashboard/composables/useDetectKeyboardLayout';
import { SHORTCUT_KEYS, KEYS } from './constants';
import {
  LAYOUT_QWERTZ,
  keysToModifyInQWERTZ,
} from 'shared/helpers/KeyboardHelpers';
import Hotkey from 'dashboard/components/base/Hotkey.vue';

defineProps({ show: Boolean });
defineEmits(['close']);

const { t } = useI18n();
const currentLayout = ref(null);

const title = computed(() => item => {
  // Labels come exclusively from the static SHORTCUT_KEYS allowlist.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`KEYBOARD_SHORTCUTS.TITLE.${item.label}`);
});

// Added this function to check if the keySet needs a shift key
// This is used to display the shift key in the modal
// If the current layout is QWERTZ and the keySet contains a key that needs a shift key
// If layout is QWERTZ then we add the Shift+keysToModify to fix an known issue
// https://github.com/lumeratec/lumeratec/issues/9492
const needsShiftKey = computed(
  () => keySet =>
    currentLayout.value === LAYOUT_QWERTZ &&
    keySet.some(key => keysToModifyInQWERTZ.has(key))
);

onMounted(async () => {
  currentLayout.value = await useDetectKeyboardLayout();
});
</script>

<template>
  <woot-modal :show="show" size="medium" @close="() => $emit('close')">
    <div
      class="flex flex-col w-full max-h-[min(82vh,52rem)] overflow-y-auto overflow-x-hidden"
    >
      <woot-modal-header
        :header-title="$t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS')"
      />
      <div class="px-6 pt-0 pb-4 mt-6 sm:px-8">
        <div
          class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-4 rounded-xl bg-n-alpha-1"
        >
          <h5 class="text-sm font-medium text-n-slate-12">
            {{ $t('KEYBOARD_SHORTCUTS.TOGGLE_MODAL') }}
          </h5>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <Hotkey custom-class="min-h-[28px] min-w-[60px] normal-case key">
              {{ KEYS.WIN }}
            </Hotkey>
            <Hotkey custom-class="min-h-[28px] min-w-[36px] key">
              {{ KEYS.SLASH }}
            </Hotkey>
          </div>
        </div>
      </div>

      <div
        class="grid grid-cols-1 px-6 pt-0 pb-8 gap-x-10 gap-y-2 sm:px-8 lg:grid-cols-2"
      >
        <div
          v-for="shortcut in SHORTCUT_KEYS"
          :key="shortcut.id"
          class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 min-w-0 py-2 border-b border-n-weak"
        >
          <h5 class="min-w-0 text-sm leading-5 break-words text-n-slate-12">
            {{ title(shortcut) }}
          </h5>
          <div class="flex flex-wrap items-center justify-end gap-2 max-w-64">
            <template v-if="needsShiftKey(shortcut.keySet)">
              <Hotkey custom-class="min-h-[28px] min-w-[36px] key">
                {{ KEYS.SHIFT }}
              </Hotkey>
            </template>

            <template v-for="(key, index) in shortcut.displayKeys" :key="index">
              <template v-if="key !== KEYS.SLASH">
                <Hotkey
                  custom-class="min-h-[28px] min-w-[36px] key normal-case"
                >
                  {{ key }}
                </Hotkey>
              </template>
              <span
                v-else
                class="flex items-center text-sm font-semibold text-n-slate-12"
              >
                {{ key }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.key {
  @apply py-2 px-2.5 font-semibold text-xs text-n-slate-12 bg-n-slate-4 dark:bg-n-slate-2 shadow border-b-2 rtl:border-l-2 ltr:border-r-2 border-n-strong;
}
</style>
